import { normalizeDesignTokenValue } from './values';

// A shadow's color may link a color token by reference; no other CSS variable is allowed
const TOKEN_COLOR = /^var\(--sc-token-[a-z][a-z0-9-]*\)$/;
const TOKEN_COLORS = /var\(--sc-token-[a-z][a-z0-9-]*\)/g;

export type DesignPresetShadow = {
	x: number;
	y: number;
	blur?: number;
	spread?: number;
	inset: boolean;
	color?: string;
};
export type DesignPresetShadowProperty = 'boxShadow' | 'textShadow';

/** Preset shadows use px lengths; CSS also permits a unitless zero. */
export function parseDesignPresetShadows(property: DesignPresetShadowProperty, value: unknown): DesignPresetShadow[] {
	const invalid = () =>
		new Error(
			`Invalid ${property}: use two offsets, optional non-negative blur${property === 'boxShadow' ? ', optional spread/inset' : ''} and a color. Lengths require px (or 0).`
		);
	if (
		typeof value !== 'string' ||
		!value.trim() ||
		/[;{}<>\n\r]|var\(|calc\(|url\(/i.test(value.replace(TOKEN_COLORS, ''))
	)
		throw invalid();
	if (value.trim() === 'none') return [];
	return value
		.trim()
		.split(/,(?![^()]*\))/)
		.map(part => {
			const entries = part.trim().match(/[a-z]+\([^()]*\)|[^\s]+/gi) ?? [];
			const lengths: number[] = [];
			let inset = false;
			let color: string | undefined;
			for (const entry of entries) {
				if (entry === 'inset') {
					if (inset || property === 'textShadow') throw invalid();
					inset = true;
				} else if (/^[-+]?(?:\d+(?:\.\d+)?|\.\d+)px$/i.test(entry) || /^[-+]?0(?:\.0+)?$/.test(entry)) {
					lengths.push(parseFloat(entry));
				} else {
					if (color) throw invalid();
					if (TOKEN_COLOR.test(entry)) {
						color = entry;
						continue;
					}
					try {
						color = normalizeDesignTokenValue('color', entry);
					} catch {
						throw invalid();
					}
				}
			}
			if (lengths.length < 2 || lengths.length > (property === 'boxShadow' ? 4 : 3) || (lengths[2] ?? 0) < 0)
				throw invalid();
			return { x: lengths[0], y: lengths[1], blur: lengths[2], spread: lengths[3], inset, color };
		});
}

export function stringifyDesignPresetShadows(shadows: DesignPresetShadow[]): string {
	return shadows.length
		? shadows
				.map(shadow =>
					[
						...(shadow.inset ? ['inset'] : []),
						`${shadow.x}px`,
						`${shadow.y}px`,
						...(shadow.blur !== undefined || shadow.spread !== undefined ? [`${shadow.blur ?? 0}px`] : []),
						...(shadow.spread !== undefined ? [`${shadow.spread}px`] : []),
						...(shadow.color ? [shadow.color] : []),
					].join(' ')
				)
				.join(', ')
		: 'none';
}
